import React, { JSX, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import { ExpandMore } from './ExpandMore';
import { Box, useMediaQuery, useTheme, SxProps, Theme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { StyledMarkdown } from './StyledMarkdown';
import { BackstoryElementProps } from './BackstoryTab';
import {
  ChatMessage,
  ChatSession,
  ChatMessageMetaData,
  ApiActivityType,
  ChatMessageUser,
  ChatMessageError,
  ChatMessageStatus,
  ChatSenderType,
} from 'types/types';
import { MessageContainer } from './MessageContainer';
import { MessageMeta } from './MessageMeta';
import { getMessageStyle } from './messageStyles';

interface MessageProps extends BackstoryElementProps {
  message: ChatMessageUser | ChatMessage | ChatMessageError | ChatMessageStatus;
  title?: string;
  chatSession?: ChatSession;
  className?: string;
  sx?: SxProps<Theme>;
  viewAsMarkdown?: boolean; // Whether to render messages as markdown
  expandable?: boolean;
  expanded?: boolean;
  onExpand?: (open: boolean) => void;
}

const resolveMessageType = (
  message: MessageProps['message']
): ApiActivityType | ChatSenderType | 'error' => {
  if ('activity' in message) return message.activity;
  if ('error' in message) return 'error';
  return (message as ChatMessage).role;
};

const resolveMetadata = (message: MessageProps['message']): ChatMessageMetaData | null => {
  const metadata: ChatMessageMetaData | null =
    'metadata' in message ? (message.metadata as ChatMessageMetaData) || null : null;
  if ('role' in message && message.role !== 'assistant') return null;
  return metadata;
};

const Message = (props: MessageProps): JSX.Element => {
  const {
    message,
    title,
    sx,
    className,
    chatSession,
    onExpand,
    expanded,
    expandable,
    viewAsMarkdown = true,
  } = props;
  const [metaExpanded, setMetaExpanded] = useState<boolean>(false);
  const theme = useTheme();
  const type = resolveMessageType(message);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const style = getMessageStyle(theme, type, isMobile);

  const handleMetaExpandClick = (): void => {
    setMetaExpanded(!metaExpanded);
  };

  let content;
  if (typeof message.content === 'string') {
    content = message.content.trim();
  } else {
    console.error(`message content is not a string, it is a ${typeof message.content}`);
    return <></>;
  }

  if (!content) {
    return <></>;
  }

  const messageView = (
    <>
      {viewAsMarkdown && (
        <StyledMarkdown
          chatSession={chatSession}
          streaming={message.status === 'streaming'}
          content={content}
        />
      )}
      {!viewAsMarkdown && (
        <Box>
          <pre>{content}</pre>
        </Box>
      )}
    </>
  );

  let metadataView = <></>;
  const metadata = resolveMetadata(message);
  if (metadata) {
    metadataView = (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexDirection: 'row',
          }}
        >
          <Box sx={{ display: 'flex', flexGrow: 1 }} />
          <Button
            variant="text"
            onClick={handleMetaExpandClick}
            sx={{ flexShrink: 1, color: 'darkgrey', p: 0 }}
          >
            LLM information for this query
          </Button>
          <ExpandMore
            sx={{ flexShrink: 1 }}
            expand={metaExpanded}
            onClick={handleMetaExpandClick}
            aria-expanded={true /*message.expanded*/}
            aria-label="show more"
          >
            <ExpandMoreIcon />
          </ExpandMore>
        </Box>
        <Collapse in={metaExpanded} timeout="auto" unmountOnExit>
          <CardContent>
            <MessageMeta messageProps={props} metadata={metadata} />
          </CardContent>
        </Collapse>
      </Box>
    );
  }

  const copyContent = type === 'assistant' ? message.content : undefined;

  if (!expandable) {
    /* When not expandable, the styles are applied directly to MessageContainer */
    return (
      <>
        {messageView && (
          <MessageContainer
            copyContent={copyContent}
            type={type}
            {...{ messageView, metadataView }}
            sx={{ ...style, ...sx }}
          />
        )}
      </>
    );
  }

  // Determine if Accordion is controlled
  const isControlled = typeof expanded === 'boolean' && typeof onExpand === 'function';
  return (
    <Accordion
      expanded={isControlled ? expanded : undefined} // Omit expanded prop for uncontrolled
      defaultExpanded={expanded} // Default to collapsed for uncontrolled Accordion
      className={className}
      onChange={(_event, newExpanded): void => {
        isControlled && onExpand && onExpand(newExpanded);
      }}
      sx={{ ...sx, ...style }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        slotProps={{
          content: {
            sx: {
              display: 'flex',
              justifyItems: 'center',
              m: 0,
              p: 0,
              fontWeight: 'bold',
              fontSize: '1.1rem',
            },
          },
        }}
      >
        {title || ''}
      </AccordionSummary>
      <AccordionDetails sx={{ mt: 0, mb: 0, p: 0, pl: 2, pr: 2 }}>
        <MessageContainer
          copyContent={copyContent}
          type={type}
          {...{ messageView, metadataView }}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export type { MessageProps };

export { Message, MessageMeta };
